import Dexie, { type EntityTable } from "dexie";
import { clonePatchDocument, migratePatchDocument, type PatchDocument } from "../domain/patch";
import { createInitialPatchDocument } from "../domain/initial-patch";

type DraftRecord = {
  patchId: string;
  updatedAt: string;
  document: PatchDocument;
};

type SettingRecord = {
  key: string;
  value: string;
};

export type AttachmentBinary = {
  id: string;
  blob: Blob;
};

class PatchworkDatabase extends Dexie {
  patches!: EntityTable<PatchDocument, "id">;
  drafts!: EntityTable<DraftRecord, "patchId">;
  settings!: EntityTable<SettingRecord, "key">;
  attachments!: EntityTable<AttachmentBinary, "id">;

  constructor() {
    super("patchwork");
    this.version(1).stores({
      patches: "id, updatedAt, title",
      drafts: "patchId, updatedAt",
      settings: "key",
    });
    this.version(2).stores({
      patches: "id, updatedAt, title",
      drafts: "patchId, updatedAt",
      settings: "key",
      attachments: "id",
    });
  }
}

const database = new PatchworkDatabase();
const ACTIVE_PATCH_KEY = "activePatchId";
const memoryPatches = new Map<string, PatchDocument>();
const memoryDrafts = new Map<string, PatchDocument>();
const memorySettings = new Map<string, string>();
const memoryAttachments = new Map<string, Blob>();
let memoryActivePatchId: string | null = null;

export function persistenceAvailable() {
  return typeof indexedDB !== "undefined";
}

function documentWithNewerDraft(saved: PatchDocument, draft?: PatchDocument) {
  return draft && Date.parse(draft.updatedAt) > Date.parse(saved.updatedAt) ? draft : saved;
}

function ensureMemoryPatch() {
  if (memoryActivePatchId) return;
  const document = createInitialPatchDocument();
  memoryPatches.set(document.id, document);
  memoryActivePatchId = document.id;
}

export async function loadActivePatch(): Promise<{
  document: PatchDocument;
  recoveredDraft: boolean;
}> {
  if (!persistenceAvailable()) {
    ensureMemoryPatch();
    const saved = memoryPatches.get(memoryActivePatchId!);
    const draft = memoryDrafts.get(memoryActivePatchId!);
    const document = saved ? documentWithNewerDraft(saved, draft) : draft;
    if (!document) throw new Error("Active patch not found.");
    return {
      document: clonePatchDocument(document),
      recoveredDraft: document === draft,
    };
  }

  const setting = await database.settings.get(ACTIVE_PATCH_KEY);
  const activeId = setting?.value;
  let saved = activeId ? await database.patches.get(activeId) : undefined;
  const activeDraft = activeId ? await database.drafts.get(activeId) : undefined;

  if (!saved && activeDraft) {
    return { document: migratePatchDocument(activeDraft.document), recoveredDraft: true };
  }

  if (!saved) {
    saved = createInitialPatchDocument();
    await database.transaction("rw", database.patches, database.settings, async () => {
      await database.patches.put(saved!);
      await database.settings.put({ key: ACTIVE_PATCH_KEY, value: saved!.id });
    });
  }

  const savedDocument = migratePatchDocument(saved);
  const draft = activeDraft ?? (await database.drafts.get(savedDocument.id));
  if (draft && Date.parse(draft.updatedAt) > Date.parse(savedDocument.updatedAt)) {
    return { document: migratePatchDocument(draft.document), recoveredDraft: true };
  }

  return { document: savedDocument, recoveredDraft: false };
}

export async function listStoredPatches(): Promise<PatchDocument[]> {
  if (!persistenceAvailable()) {
    ensureMemoryPatch();
    const ids = new Set([...memoryPatches.keys(), ...memoryDrafts.keys()]);
    return [...ids]
      .map((id) => {
        const saved = memoryPatches.get(id);
        const draft = memoryDrafts.get(id);
        return saved ? documentWithNewerDraft(saved, draft) : draft!;
      })
      .map(clonePatchDocument)
      .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
  }

  const [savedDocuments, drafts] = await Promise.all([
    database.patches.toArray(),
    database.drafts.toArray(),
  ]);
  const documents = new Map(
    savedDocuments.map((document) => [document.id, migratePatchDocument(document)]),
  );
  for (const draft of drafts) {
    const draftDocument = migratePatchDocument(draft.document);
    const saved = documents.get(draft.patchId);
    if (!saved || Date.parse(draft.updatedAt) > Date.parse(saved.updatedAt)) {
      documents.set(draft.patchId, draftDocument);
    }
  }
  return [...documents.values()].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
}

export async function loadStoredPatch(
  patchId: string,
  activate = true,
): Promise<{
  document: PatchDocument;
  recoveredDraft: boolean;
}> {
  if (!persistenceAvailable()) {
    ensureMemoryPatch();
    const saved = memoryPatches.get(patchId);
    const draft = memoryDrafts.get(patchId);
    const document = saved ? documentWithNewerDraft(saved, draft) : draft;
    if (!document) throw new Error("Patch not found.");
    if (activate) memoryActivePatchId = patchId;
    return { document: clonePatchDocument(document), recoveredDraft: document === draft };
  }

  const [saved, draft] = await Promise.all([
    database.patches.get(patchId),
    database.drafts.get(patchId),
  ]);
  if (!saved && !draft) throw new Error("Patch not found.");
  if (activate) await database.settings.put({ key: ACTIVE_PATCH_KEY, value: patchId });
  if (!saved) return { document: migratePatchDocument(draft!.document), recoveredDraft: true };
  const savedDocument = migratePatchDocument(saved);
  if (draft && Date.parse(draft.updatedAt) > Date.parse(savedDocument.updatedAt)) {
    return { document: migratePatchDocument(draft.document), recoveredDraft: true };
  }
  return { document: savedDocument, recoveredDraft: false };
}

export async function persistDraft(document: PatchDocument) {
  if (!persistenceAvailable()) {
    memoryDrafts.set(document.id, clonePatchDocument(document));
    memoryActivePatchId = document.id;
    return;
  }
  await database.transaction("rw", database.drafts, database.settings, async () => {
    await database.drafts.put({
      patchId: document.id,
      updatedAt: document.updatedAt,
      document,
    });
    await database.settings.put({ key: ACTIVE_PATCH_KEY, value: document.id });
  });
}

export async function persistPatch(document: PatchDocument) {
  if (!persistenceAvailable()) {
    memoryPatches.set(document.id, clonePatchDocument(document));
    memoryDrafts.delete(document.id);
    return;
  }
  await database.transaction("rw", database.patches, database.drafts, async () => {
    await database.patches.put(document);
    await database.drafts.delete(document.id);
  });
}

export async function deleteStoredPatch(patchId: string) {
  if (!persistenceAvailable()) {
    memoryPatches.delete(patchId);
    memoryDrafts.delete(patchId);
    if (memoryActivePatchId === patchId) memoryActivePatchId = null;
    return;
  }
  await database.transaction("rw", database.patches, database.drafts, async () => {
    await database.patches.delete(patchId);
    await database.drafts.delete(patchId);
  });
}

export async function storeImportedPatches(
  documents: PatchDocument[],
  activePatchId: string,
  replaceLibrary = false,
  attachments: AttachmentBinary[] = [],
) {
  const validated = documents.map(migratePatchDocument);
  if (!validated.some((document) => document.id === activePatchId)) {
    throw new Error("Imported active patch is missing from the bundle.");
  }

  if (!persistenceAvailable()) {
    if (replaceLibrary) {
      memoryPatches.clear();
      memoryDrafts.clear();
      memoryAttachments.clear();
    }
    for (const document of validated) {
      memoryPatches.set(document.id, clonePatchDocument(document));
      memoryDrafts.delete(document.id);
    }
    for (const attachment of attachments) memoryAttachments.set(attachment.id, attachment.blob);
    memoryActivePatchId = activePatchId;
    return;
  }

  await database.transaction(
    "rw",
    database.patches,
    database.drafts,
    database.settings,
    database.attachments,
    async () => {
      if (replaceLibrary) {
        await database.patches.clear();
        await database.drafts.clear();
        await database.attachments.clear();
      }
      await database.patches.bulkPut(validated);
      await database.drafts.bulkDelete(validated.map((document) => document.id));
      if (attachments.length) await database.attachments.bulkPut(attachments);
      await database.settings.put({ key: ACTIVE_PATCH_KEY, value: activePatchId });
    },
  );
}

export async function storeAttachmentBinary(attachment: AttachmentBinary) {
  if (!persistenceAvailable()) {
    memoryAttachments.set(attachment.id, attachment.blob);
    return;
  }
  await database.attachments.put(attachment);
}

export async function loadAttachmentBinary(attachmentId: string) {
  if (!persistenceAvailable()) return memoryAttachments.get(attachmentId);
  return (await database.attachments.get(attachmentId))?.blob;
}

export async function loadAttachmentBinaries(attachmentIds: string[]) {
  const uniqueIds = [...new Set(attachmentIds)];
  const values = await Promise.all(
    uniqueIds.map(async (id) => {
      const blob = await loadAttachmentBinary(id);
      return blob ? { id, blob } : null;
    }),
  );
  return values.filter((value): value is AttachmentBinary => value !== null);
}

export async function readStoredSetting(key: string) {
  if (!persistenceAvailable()) return memorySettings.get(key);
  return (await database.settings.get(key))?.value;
}

export async function writeStoredSetting(key: string, value: string) {
  if (!persistenceAvailable()) {
    memorySettings.set(key, value);
    return;
  }
  await database.settings.put({ key, value });
}

export type StorageOverview = {
  patchCount: number;
  draftCount: number;
  documentBytes: number;
  photoBytes: number;
  audioBytes: number;
  attachmentCount: number;
  usage?: number;
  quota?: number;
  persisted: boolean;
};

export async function getStorageOverview(): Promise<StorageOverview> {
  const patches = await listStoredPatches();
  const documentBytes = new Blob(patches.map((document) => JSON.stringify(document))).size;
  const attachments = new Map(
    patches.flatMap((document) =>
      document.attachments.map((attachment) => [attachment.id, attachment] as const),
    ),
  );
  const photoBytes = [...attachments.values()]
    .filter((attachment) => attachment.kind === "photo")
    .reduce((total, attachment) => total + attachment.size, 0);
  const audioBytes = [...attachments.values()]
    .filter((attachment) => attachment.kind === "audio")
    .reduce((total, attachment) => total + attachment.size, 0);
  const draftCount = persistenceAvailable() ? await database.drafts.count() : memoryDrafts.size;
  const estimate = await navigator.storage?.estimate?.();
  const persisted = await navigator.storage?.persisted?.();
  return {
    patchCount: patches.length,
    draftCount,
    documentBytes,
    photoBytes,
    audioBytes,
    attachmentCount: attachments.size,
    usage: estimate?.usage,
    quota: estimate?.quota,
    persisted: persisted ?? false,
  };
}
